import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Path, Plus, Trash, Truck, MapPin, Clock, PencilSimple, CheckCircle } from "@phosphor-icons/react";
import { EQUIPMENT_TYPES } from "@/lib/constants";
import { toast } from "sonner";

const STATUSES = [
  { value: "On Load", chip: "bg-blue-50 text-blue-700 ring-blue-200" },
  { value: "En Route", chip: "bg-violet-50 text-violet-700 ring-violet-200" },
  { value: "Going Empty", chip: "bg-amber-50 text-amber-700 ring-amber-200" },
  { value: "Available", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
];
const chipFor = (s) => STATUSES.find((x) => x.value === s)?.chip || "bg-slate-100 text-slate-600 ring-slate-200";
const empty = { carrier_id: "", carrier_name: "", driver: "", current_location: "", current_load: "", status: "On Load", empty_location: "", empty_datetime: "", truck_type: "Dry Van" };
const fmt = (d) => (d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function RoutePlanning() {
  const { user } = useAuth();
  const { data: plans = [], isLoading } = useList("route-plans", "/route-plans", { refetchInterval: 15000 });
  const { data: carriers = [] } = useList("carriers", "/carriers");
  const { data: drivers = [] } = useList("drivers", "/drivers");
  const invalidate = useInvalidate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const inp = "bg-white border-slate-300";

  const carrierDrivers = drivers.filter((d) => d.carrier_id === form.carrier_id);
  const counts = {
    available: plans.filter((p) => p.status === "Available").length,
    empty: plans.filter((p) => p.status === "Going Empty").length,
    onload: plans.filter((p) => p.status === "On Load" || p.status === "En Route").length,
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };

  const save = async () => {
    if (!form.carrier_id || !form.driver) { toast.error("Select a carrier and driver"); return; }
    setSaving(true);
    try {
      const carrier_name = carriers.find((c) => c.id === form.carrier_id)?.carrier_name || form.carrier_name || "";
      const payload = { ...form, carrier_name, assigned_dispatcher: user.name, assigned_dispatcher_id: user.id };
      if (editing) await api.put(`/route-plans/${editing.id}`, payload);
      else await api.post("/route-plans", payload);
      toast.success(editing ? "Status updated" : "Carrier status added");
      setForm(empty); setOpen(false); invalidate("route-plans");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this record?")) return; await api.delete(`/route-plans/${id}`); invalidate("route-plans"); };

  return (
    <div>
      <PageHeader title="Carrier Status" subtitle="Which carrier is going empty, where, and when — plan the next load before the truck sits"
        actions={<Button data-testid="rp-open-btn" onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold"><Plus size={18} weight="bold" className="mr-1.5" />Update Status</Button>} />

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Panel className="p-4"><p className="text-xs text-slate-500">Available Now</p><p className="text-3xl font-heading font-extrabold text-emerald-600 tabular">{counts.available}</p></Panel>
        <Panel className="p-4"><p className="text-xs text-slate-500">Going Empty Soon</p><p className="text-3xl font-heading font-extrabold text-amber-600 tabular">{counts.empty}</p></Panel>
        <Panel className="p-4"><p className="text-xs text-slate-500">On Load / En Route</p><p className="text-3xl font-heading font-extrabold text-blue-600 tabular">{counts.onload}</p></Panel>
      </div>

      <Panel className="p-0">
        {isLoading ? <p className="p-6 text-slate-400">Loading…</p> : plans.length === 0 ? (
          <EmptyState icon={Path} title="No carrier status records yet" hint="Click 'Update Status' to track where each carrier is and when it goes empty." action={<Button onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700"><Plus size={16} className="mr-1" />Update Status</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead>Carrier / Driver</TableHead><TableHead>Status</TableHead><TableHead>Current Location</TableHead><TableHead>Current Load</TableHead><TableHead>Going Empty At</TableHead><TableHead>Empty On</TableHead><TableHead>Equipment</TableHead><TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((p) => (
                  <TableRow key={p.id} className="border-slate-100" data-testid={`rp-row-${p.id}`}>
                    <TableCell><p className="font-medium text-slate-900">{p.carrier_name}</p><p className="text-xs text-slate-500">{p.driver}</p></TableCell>
                    <TableCell><span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ring-inset ${chipFor(p.status)}`}>{p.status || "—"}</span></TableCell>
                    <TableCell className="text-sm text-slate-600"><span className="inline-flex items-center gap-1"><MapPin size={13} className="text-blue-500" />{p.current_location || "—"}</span></TableCell>
                    <TableCell className="text-sm text-slate-600 font-mono">{p.current_load || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{p.empty_location || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600"><span className="inline-flex items-center gap-1"><Clock size={13} className="text-amber-500" />{fmt(p.empty_datetime)}</span></TableCell>
                    <TableCell><span className="inline-flex items-center gap-1 text-sm text-slate-700"><Truck size={14} className="text-slate-400" />{p.truck_type}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(p)} className="p-1.5 text-slate-400 hover:text-blue-600" data-testid={`rp-edit-${p.id}`}><PencilSimple size={15} /></button>
                        <button onClick={() => remove(p.id)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`rp-del-${p.id}`}><Trash size={15} /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Path size={20} weight="duotone" className="text-blue-600" />{editing ? "Update Carrier Status" : "Add Carrier Status"}</DialogTitle><DialogDescription className="sr-only">Carrier status</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <Field label="Carrier *">
              <Select value={form.carrier_id} onValueChange={(v) => setForm({ ...form, carrier_id: v, driver: "" })}>
                <SelectTrigger className={inp} data-testid="rp-carrier"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                <SelectContent>{carriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.carrier_name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Driver *">
              <Select value={form.driver} onValueChange={(v) => setForm({ ...form, driver: v })} disabled={!form.carrier_id}>
                <SelectTrigger className={inp} data-testid="rp-driver"><SelectValue placeholder={form.carrier_id ? "Select driver" : "Pick carrier first"} /></SelectTrigger>
                <SelectContent>{carrierDrivers.length === 0 ? <SelectItem value="none" disabled>No drivers</SelectItem> : carrierDrivers.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className={inp} data-testid="rp-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.value}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Current Location"><Input data-testid="rp-current-loc" value={form.current_location} onChange={(e) => setForm({ ...form, current_location: e.target.value })} className={inp} placeholder="e.g. Memphis, TN" /></Field>
            <Field label="Current Load #"><Input data-testid="rp-current-load" value={form.current_load} onChange={(e) => setForm({ ...form, current_load: e.target.value })} className={`${inp} font-mono`} placeholder="Load / PO #" /></Field>
            <Field label="Going Empty At"><Input data-testid="rp-location" value={form.empty_location} onChange={(e) => setForm({ ...form, empty_location: e.target.value })} className={inp} placeholder="e.g. Dallas, TX" /></Field>
            <Field label="Empty On (date & time)"><Input type="datetime-local" data-testid="rp-empty" value={form.empty_datetime} onChange={(e) => setForm({ ...form, empty_datetime: e.target.value })} className={inp} /></Field>
            <Field label="Equipment">
              <Select value={form.truck_type} onValueChange={(v) => setForm({ ...form, truck_type: v })}>
                <SelectTrigger className={inp} data-testid="rp-truck"><SelectValue /></SelectTrigger>
                <SelectContent>{EQUIPMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="rp-add-btn">{saving ? "Saving…" : editing ? "Save" : "Add Status"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
